import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Api from '../../services/api';
import { capitalize } from '../../utils/string';
import { useAuth } from '../../context/AuthContext';
// os components:
import { Link, useNavigate } from 'react-router';
import {
    groupNameBySlug,
    shortGroupNameBySlug,
    status as statusSlug
} from '../../utils/mappings';
import {
    EditOutlined,
    SearchOutlined,
    LoadingOutlined,
    FileTextOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import {
    Table,
    Spin,
    Row,
    Button,
    Space,
    Tooltip,
    Modal,
    Typography,
    Select,
    Tag,
    DatePicker,
    Alert,
    Popconfirm,
    Input,
    Flex,
    Divider,
    Result,
    Col,
    Checkbox
} from 'antd';
import { Centered } from '../../components';
import CardStatistic from '../../components/CardStatistic';

const flexProps = {
    xs: { flex: '100%' },
    sm: { flex: '50%' },
    md: { flex: '20%' },
    lg: { flex: '20%' },
    xl: { flex: '20%' },
}

// os status de relatórios elegíveis à exclusão:
const deletableStatuses = [
    statusSlug.RASCUNHO,
    statusSlug.EDITADO,
    statusSlug.AGUARDANDO_CORRECAO,
    // ...
]

export default function Reports() {
    const navigate = useNavigate();
    const { user, userGroups, authUserBelongsToGroups } = useAuth();

    const firstSubcorregedoriaGroup = 'lotacao:primeira_subcorregedoria_geral';
    const secondSubcorregedoriaGroup = 'lotacao:segunda_subcorregedoria_geral';

    const defaultAssignedGroupName = authUserBelongsToGroups([firstSubcorregedoriaGroup])
        ? firstSubcorregedoriaGroup
        : authUserBelongsToGroups([secondSubcorregedoriaGroup])
            ? secondSubcorregedoriaGroup
            : null;

    const shouldSetDefaultUserParams = Boolean(defaultAssignedGroupName);

    const [isFetching, setIsFetching] = useState(false);
    const [isCreatingReport, setIsCreatingReport] = useState(false);
    const [isValidatingReport, setIsValidatingReport] = useState(false);
    const [existingReportFound, setExistingReportFound] = useState(null);
    const [reports, setReports] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [defenders, setDefenders] = useState([]);
    const [reportTemplateId, setReportTemplateId] = useState(null);
    const [reportDefenderId, setReportDefenderId] = useState(null);
    const [reportStartDate, setReportStartDate] = useState(null);
    const [reportAssignedGroup, setReportAssignedGroup] = useState(null);
    const [reportEndDate, setReportEndDate] = useState(null);
    const [reportSelectedDate, setReportSelectedDate] = useState(null);
    const [reportIsTest, setReportIsTest] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [exclusionPassphrase, setExclusionPassphrase] = useState(null);
    const [entryExclusionPassphrase, setEntryExclusionPassphrase] = useState(null);
    const [pagination, setPagination] = useState({});
    const [fetchingPaginated, setFetchingPaginated] = useState(null);
    const [statisctcsRma, setStatisticsRma] = useState(null);
    const [reportParams, setReportParams] = useState(() => (
        defaultAssignedGroupName
            ? { assigned_group_name: defaultAssignedGroupName }
            : {}
    ));
    const [responseStatusError, setResponseStatusError] = useState(null);

    /**
        * Recupera as estatísticas dos RMAs.
        * Recebe o nome do setor (opcional) para repassar como query param.
     */
    async function fetchStatisticRma(group_name = null) {
        try {
            const endpoint = group_name 
                ? `reports/statistics/?group_name=${group_name}` 
                : 'reports/statistics/';
            const response = await Api(endpoint).index();
            setStatisticsRma(response.data);
        } catch (error){
            setResponseStatusError(error.status)
        }
    }

    /**
     * Recupera os relatórios criados.
     * 
     * @param {String} targetUrl
     * @param {Object} params
     */
    const fetchReports = async (targetUrl, params) => {
        setIsFetching(true);
        
        try {
            const { data } = await Api(targetUrl ?? 'reports').index(params);
            
            setReports(data.results);
            setPagination({
                previous: data.previous,
                next: data.next
            });
        } catch (error) {
            setResponseStatusError(error.status)
        }

        setIsFetching(false);
    }

    /**
     * Recupera os modelos de relatórios cadastrados.
     */
    const fetchTemplates = async () => {
        try {
            const response = await Api('templates').index();
    
            setTemplates(response.data);
        } catch (error) {
            setResponseStatusError(error.status)
        }
    }

    /**
     * Recupera os defensores.
     */
    const fetchDefenders = async () => {
        try {

            const response = await Api('defenders', { noPrefix: true }).index();
    
            setDefenders(response.data);
        } catch (error) {
            setResponseStatusError(error.status)
        }
    }

    /**
     * Inicia a busca pelos recursos da tela.
     */
    const fetchResources = async () => {
        setIsFetching(true);

        const resourcesToLoad = [
            fetchTemplates(),
            fetchDefenders()
        ]

        if (!shouldSetDefaultUserParams) {
            resourcesToLoad.push(fetchReports())
        }

        await Promise.all(resourcesToLoad)

        setIsFetching(false);
    }

    useEffect(() => {
        document.title = 'DPEMT - Relatórios Mensais de Atividades';

        fetchResources();
    }, []);

    useEffect(() => {
        fetchStatisticRma(reportParams.assigned_group_name ?? null);
    }, [reportParams.assigned_group_name]);

    useEffect(() => {
        if (!shouldSetDefaultUserParams || reportParams.assigned_group_name) {
            return;
        }

        setReportParams(previousReportParams => ({
            ...previousReportParams,
            assigned_group_name: defaultAssignedGroupName
        }));
    }, [
        shouldSetDefaultUserParams,
        defaultAssignedGroupName,
        reportParams.assigned_group_name,
        userGroups
    ]);

    useEffect(() => {
        if (Object.keys(reportParams).length > 0) {
            fetchReports(null, reportParams);
        }
    }, [reportParams]);

    /**
     * Com base nos parâmetros submetidos para criação
     * de um novo relatório, verifica se já não existe um
     * registro com os mesmos valores.
     */
    async function fetchExistingReport() {
        setIsValidatingReport(true);
        setExistingReportFound(null);

        try {
            const response = await Api('reports/find_existing_id').store({
                template_id: reportTemplateId,
                defender_id: reportDefenderId,
                start_date: reportStartDate,
                end_date: reportEndDate
            });

            setExistingReportFound(response.data);
        } catch {}

        setIsValidatingReport(false);
    }

    useEffect(() => {
        if (reportDefenderId && reportEndDate) {
            fetchExistingReport();
        }
    }, [reportDefenderId, reportEndDate]);

    /**
     * Verifica se a data selecionada está
     * habilitada para criação.
     * 
     * @param {Date} targetDate
     * @returns {Boolean}
     */
    const isBeyondCreationTreshold = targetDate => {
        if (reportIsTest) {
            return true;
        }

        const previousMonth = dayjs().subtract(1, 'month').format('MM/YYYY');
        const isPreviousMonth = targetDate.format('MM/YYYY') === previousMonth;
        const isBeyondTreshold = Number(dayjs().format('D')) > process.env.REACT_APP_ALLOW_REPORT_INIT_AFTER_DAY;

        if (isPreviousMonth && !isBeyondTreshold) {
            return false;
        }

        return true;
    };

    /**
     * Define o alerta que será exibido no modal
     * de criação, com base no estado do componente.
     * 
     * @returns {React.JSX.Element}
     */
    const FormModalAlert = () => {
        if (isCreatingReport) {
            return (
                <Alert
                    banner
                    type="info"
                    message={                        
                        <Typography.Text>
                            A criação deste relatório pode demorar alguns segundos, pois requer a coleta de dados em sistemas diversos.
                        </Typography.Text>
                    }
                />
            )
        }

        if (reportSelectedDate && !isBeyondCreationTreshold(reportSelectedDate)) {
            return (
                <Alert
                    banner
                    type="error"
                    message={
                        <Typography.Text>
                            Não é possível iniciar o RMA de <b>{reportSelectedDate.format('MM/YYYY')}</b> pois ainda <u>existem dados sendo computados</u>. A criação será habilitada <b>após o dia {process.env.REACT_APP_ALLOW_REPORT_INIT_AFTER_DAY}</b> do mês corrente.
                        </Typography.Text>
                    }
                />
            );
        }

        if (existingReportFound?.report_id) {
            if (existingReportFound.is_test) {
                return (
                    <Alert
                        banner
                        type="error"
                        message={
                            <Typography.Text>
                                {capitalize(user.first_name)}, encontramos um relatório experimental (sem validade) com esses atributos. Exclua-o para iniciar um novo RMA.
                            </Typography.Text>
                        }
                    />
                );
            }

            const message = existingReportFound.is_approved ? (
                <Typography.Text>
                    {capitalize(user.first_name)}, encontramos um relatório já <b>homologado</b> com esses atributos, tornando, assim, vedada a criação de um novo RMA.<br/>
                    <Link to={`/reports/${existingReportFound.report_id}`} target="_blank">Clique aqui</Link> para visualiza-lo.<br/>
                </Typography.Text>
            ) : (
                <Typography.Text>
                    {capitalize(user.first_name)}, encontramos um <b><Link to={`/reports/${existingReportFound.report_id}`} target="_blank">relatório</Link></b> com esses mesmos atributos, destinado à <b>{groupNameBySlug[existingReportFound.assigned_group_name]}</b>.<br />Se aplicável, exclua-o para iniciar um novo.
                </Typography.Text>
            )

            return (
                <Alert
                    banner
                    type={existingReportFound.is_approved ? "error" : "warning"}
                    message={message}
                />
            );
        }
    };

    if (responseStatusError) {
        return (
            <Centered>
                <Result
                    status={responseStatusError}
                    subTitle={
                        <Typography.Title level={4}>
                            Não foi possível carregar este recurso.
                        </Typography.Title>
                    }
                    extra={
                        <Typography.Text>
                            Se acredita ser um erro, entre em contato com <a href="mailto:suportesistemas@dp.mt.gov.br">suportesistemas@dp.mt.gov.br</a>
                        </Typography.Text>
                    }
                />
            </Centered>
        );
    }

    /**
     * Envia uma requisição para excluir o relatório
     * especificado.
     * 
     * @param {Number} reportId o identificador do relatório a ser excluído
     */
    async function handleReportExclusion(reportId) {
        try {
            await Api('reports').delete(reportId);

            setReports(previousReports => [
                ...previousReports.filter(report => report.id !== reportId)
            ])

            if (existingReportFound?.report_id == reportId) {
                setExistingReportFound(null);
            }
        } catch {}
    }

    const userCanAssignGroup = authUserBelongsToGroups([
        'superadministrador',
        'administrador',
        'lotacao:secretaria_da_corregedoria_geral'
    ])

    const groupOptions = [
        {
            value: 'lotacao:primeira_subcorregedoria_geral',
            label: 'Primeira Subcorregedoria-Geral'
        },
        {
            value: 'lotacao:segunda_subcorregedoria_geral',
            label: 'Segunda Subcorregedoria-Geral'
        },
    ]

    const tagByStatusSlug = {
        rascunho: <Tag color="grey">Rascunho</Tag>,
        editado: <Tag color="orange">Em edição</Tag>,
        notificado: <Tag color="blue">Notificado</Tag>,
        falha_na_notificacao: <Tag color="red">Falha na notificação</Tag>,
        aguardando_correcao: <Tag color="purple">Aguardando correção</Tag>,
        aguardando_manifestacao: <Tag>Aguardando manifestação</Tag>,
        manifestacao_recebida: <Tag color="black">Manifestação recebida</Tag>,
        homologado: <Tag color="green">Homologado</Tag>
    }

    const tableColumns = [
        {
            title: '#',
            dataIndex: 'reportId',
            key: 'reportId',
        },
        {
            title: 'Destino',
            dataIndex: 'destino',
            key: 'destino',
        },
        {
            title: 'Defensor',
            dataIndex: 'defensor',
            key: 'defensor',
        },
        {
            title: 'Modelo',
            dataIndex: 'modelo',
            key: 'modelo',
        },
        {
            title: 'Situação',
            key: 'situacao',
            render: (_, record) => record.teste ? '⚠️ TESTE' : tagByStatusSlug[record.status]
        },
        {
            title: 'Referência',
            dataIndex: 'referencia',
            key: 'referencia',
        },
        {
            title: 'Notificado em',
            dataIndex: 'notificadoEm',
            key: 'notificadoEm',
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Visualizar">
                        <Button type="link" icon={<SearchOutlined style={{ color: record.teste ? 'white' : 'default' }} />} onClick={() => navigate(`/reports/${record.reportId}`)} />
                    </Tooltip>

                    <Tooltip title="Visualizar em PDF">
                        <Link target="_blank" to={`${process.env.REACT_APP_API_URL}/api/v1/reports/${record.reportId}/download`}>
                            <FileTextOutlined style={{ fontSize: 16, color: record.teste ? 'white' : 'default' }} />
                        </Link>
                    </Tooltip>

                    {deletableStatuses.includes(record.status) && (
                        <Popconfirm
                            title="Excluir RMA"
                            onOpenChange={isOpen => {
                                if (isOpen) {
                                    // gera a palavra de segurança:
                                    const passphrase = record.defensor.split(' ')[0].toLowerCase();

                                    setExclusionPassphrase(`rma-${passphrase}-${record.referencia}`);
                                } else {
                                    setExclusionPassphrase(null);
                                    setEntryExclusionPassphrase(null);
                                }
                            }}
                            description={(
                                <div style={{ margin: '15px 0' }}>
                                    <Typography.Text>
                                        Para confirmar a exclusão, digite a palavra de segurança<br/><b>{exclusionPassphrase}</b>:
                                    </Typography.Text>

                                    <Input
                                        style={{ marginTop: 5 }}
                                        placeholder={exclusionPassphrase}
                                        onChange={({ target }) => setEntryExclusionPassphrase(target.value.trim())}
                                        value={entryExclusionPassphrase}
                                    />
                                </div>
                            )}
                            placement="leftTop"
                            onConfirm={() => handleReportExclusion(record.reportId)}
                            okText="Excluir"
                            cancelText="Cancelar"
                            okButtonProps={{
                                danger: true,
                                disabled: entryExclusionPassphrase !== exclusionPassphrase
                            }}
                        >
                            <Tooltip title="Excluir RMA">
                                <Button danger type="link" icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm >
                    )}
                </Space>
            )
        }
    ]

    const renderNotificationCell = value => {
        // se uma notificação ainda não foi enviada:
        if (!value) {
            return;
        }

        // se houve falha na notificação:
        if (value === 'falha_na_notificacao') {
            return tagByStatusSlug.falha_na_notificacao
        }
        
        return dayjs(value).format('DD/MM/YYYY')
    }

    /**
     * Mapeia os registros os relatórios para
     * o formato esperado pela tabela.
     * 
     * @param {Array} data 
     * @returns {Object}
     */
    const toDataSource = data => data.map(row => ({
        key: row.id,
        teste: row.is_test,
        destino: row.is_test ? '⚠️ TESTE' : shortGroupNameBySlug[row.assigned_group.name],
        reportId: row.id,
        defensor: row.defender.user.full_name,
        modelo: row.template_name,
        status: row.last_history.status.slug,
        referencia: dayjs(row.end_date).format('MM/YYYY'),
        aprovadoEm: row.approved_at ? dayjs(row.approved_at).format('DD/MM/YYYY') : null,
        notificadoEm: renderNotificationCell(row.notified_at)
    }));

    /**
     * Submete os dados do relatório para criação.
     */
    async function createReport() {
        setIsCreatingReport(true);

        try {
            const response = await Api('reports').store({
                template_id: reportTemplateId,
                defender_id: reportDefenderId,
                start_date: reportStartDate,
                end_date: reportEndDate,
                assigned_group_name: reportAssignedGroup,
                is_test: reportIsTest
            });

            navigate(`/reports/${response.data.id}`);
        } catch {}

        setIsCreatingReport(false);
    }

    function handleReportParamChange(inputName, value) {
        setReportParams({
            ...reportParams,
            [inputName]: value
        })
    }

    if (isFetching) {
        return (
            <Centered>
                <Space direction="vertical" align="center" size="large">
                    <Spin indicator={<LoadingOutlined spin />} size="large" />

                    <Typography.Title level={5}>
                        Em alguns instantes os relatórios aparecerão aqui.
                    </Typography.Title>
                </Space>
            </Centered>
        );
    }

    return (
        <>
            <Flex justify="space-between">
                <Typography.Title level={2} style={{ margin: 0 }}>
                    Relatórios Mensais de Atividades
                </Typography.Title>

                {!authUserBelongsToGroups(['lotacao:escola_superior', 'leitura']) && (
                    <Button onClick={() => setReportModalOpen(true)} type="primary" icon={<EditOutlined />}>
                        Iniciar RMA
                    </Button>
                )}
            </Flex>
        
            {statisctcsRma && (               
                <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
                    <Col span={6}>
                        <CardStatistic 
                            title="Total de RMAs em Edição" 
                            valueTotal={statisctcsRma?.total_em_edicao}
                            valuePercent={statisctcsRma?.percent_em_edicao} 
                            colorBack='#fff7e6'
                            colorText= '#e07f2c'/>
                    </Col>                
                    <Col span={6}>
                        <CardStatistic 
                            title="Total de RMAs Aguardando Correção"
                            valueTotal={statisctcsRma?.total_aguardando_correcao}
                            valuePercent={statisctcsRma?.percent_aguardando_correcao} 
                            colorBack='#f9f0ff' 
                            colorText='#531dab'/>                                    
                    </Col>
                    <Col span={6}>
                        <CardStatistic 
                            title="Total de RMAs Aguardando Manifestação"
                            valueTotal={statisctcsRma?.total_aguardando_manifestacao}
                            valuePercent={statisctcsRma?.percent_aguardando_manifestacao} 
                            colorBack='#fafafa' 
                            colorText='#1e1e1e'/>                                    
                    </Col>
                    <Col span={6}>
                        <CardStatistic 
                            title="Total de RMAs com Manifestação Recebida"
                            valueTotal={statisctcsRma?.total_manifestacao_recebida}
                            valuePercent={statisctcsRma?.percent_manifestacao_recebida} 
                            colorBack='#000000'
                            colorText = '#ffffff'/>
                    </Col>
                </Row>
            )} 

            {!statisctcsRma && ( 
                  <Divider style={{ margin: '.5rem 0 .9rem 0' }} />
            )}   
            
            <Row gutter={6} justify="end" style={{ marginBottom: 12 }}>
                <Col {...flexProps}>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Filtrar por Grupo"
                        value={reportParams.assigned_group_name}
                        options={groupOptions}
                        onChange={value => {
                            handleReportParamChange('assigned_group_name', value);
                        }}
                    />
                </Col>

                <Col {...flexProps}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filtrar por Defensor"
                        showSearch
                        allowClear
                        value={reportParams.defender_id}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={defenders.map(defender => ({
                            value: defender.id,
                            label: defender.user.full_name
                        }))}
                        onChange={value => handleReportParamChange('defender_id', value)}
                    />
                </Col>

                <Col {...flexProps}>
                    <DatePicker
                        placeholder="Filtrar por Mês"
                        style={{ width: '100%' }}
                        picker="month"
                        format={{
                            format: 'MM/YYYY',
                            type: 'mask'
                        }}
                        allowClear
                        value={reportParams?.date_ref ? dayjs(reportParams.date_ref) : null}
                        onChange={(selectedMonth) => {
                            if (!selectedMonth) {
                                handleReportParamChange('date_ref', null);

                                return;
                            }

                            handleReportParamChange('date_ref', selectedMonth.format('YYYY-MM'));
                        }}
                    />
                </Col>

                <Col {...flexProps}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filtrar por Modelo"
                        showSearch
                        allowClear
                        value={reportParams.template_id}
                        options={templates.map(template => ({
                            value: template.id,
                            label: template.name
                        }))}
                        onChange={value => handleReportParamChange('template_id', value)}
                    />
                </Col>

                <Col {...flexProps}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filtrar por Situação"
                        showSearch
                        allowClear
                        value={reportParams.status_slug}
                        options={[
                            { value: "aguardando_correcao", label: tagByStatusSlug.aguardando_correcao },
                            { value: "manifestacao_recebida", label: tagByStatusSlug.manifestacao_recebida },
                            { value: "aguardando_manifestacao", label: tagByStatusSlug.aguardando_manifestacao },
                            { value: "homologado", label: tagByStatusSlug.homologado },
                            { value: "notificado", label: tagByStatusSlug.notificado },
                            { value: "editado", label: tagByStatusSlug.editado },
                            { value: "rascunho", label: tagByStatusSlug.rascunho },
                            { value: "falha_na_notificacao", label: tagByStatusSlug.falha_na_notificacao },
                        ]}
                        onChange={value => handleReportParamChange('status_slug', value)}
                    />
                </Col>
            </Row>

            <Table
                dataSource={toDataSource(reports)}
                columns={tableColumns}
                pagination={false}
                onRow={report => ({
                    style: {
                        background: report.teste ? '#1677ff' : 'default',
                        color: report.teste ? 'white' : 'default'
                    }
                })}
            />

            <Flex justify="space-between" style={{ marginTop: 15 }}>
                <Button
                    disabled={!pagination.previous}
                    loading={fetchingPaginated === 'previous'}
                    onClick={() => {
                        setFetchingPaginated('previous');
                        fetchReports(pagination.previous);
                        setFetchingPaginated(null);
                    }}
                >
                    Anterior
                </Button>

                <Button
                    disabled={!pagination.next}
                    loading={fetchingPaginated === 'next'}
                    onClick={() => {
                        setFetchingPaginated('next');
                        fetchReports(pagination.next);
                        setFetchingPaginated(null);
                    }}
                >
                    Próximo
                </Button>
            </Flex>

            <Modal
                centered
                maskClosable={false}
                open={reportModalOpen}
                footer={() => {
                    const canCreate = !existingReportFound?.report_id && (
                        reportSelectedDate && isBeyondCreationTreshold(reportSelectedDate)
                    );

                    return (
                        <Flex justify="space-between">
                            <Button onClick={() => setReportModalOpen(false)}>
                                Cancelar
                            </Button>

                            {canCreate && (
                                <Button
                                    type="primary"
                                    onClick={createReport}
                                    loading={isValidatingReport || isCreatingReport}
                                    disabled={
                                        !reportStartDate
                                        || !reportEndDate
                                        || (
                                            userCanAssignGroup
                                            && !reportIsTest
                                            && !reportAssignedGroup
                                        )
                                    }
                                >
                                    {isCreatingReport ? 'Iniciando' : 'Iniciar'} {reportIsTest ? 'Teste' : 'RMA'}
                                </Button>
                            )}
                        </Flex>
                    );
                }}
                onCancel={() => setReportModalOpen(false)}
            >
                <Row style={{ marginBottom: '1rem' }}>
                    <Typography.Title level={5}>
                        Qual <b>modelo</b> de RMA deseja preencher?
                    </Typography.Title>

                    <Select
                        onChange={setReportTemplateId}
                        size="large"
                        style={{ width: '100%' }}
                        placeholder="Selecione o modelo"
                        options={templates.filter(template => !template.disabled_at).map(template => ({
                            value: template.id,
                            label: template.name
                        }))}
                        allowClear
                        onClear={() => {
                            setReportTemplateId(null);
                            setReportDefenderId(null);
                            setReportStartDate(null);
                            setReportEndDate(null);
                        }}
                    />
                </Row>

                <Row style={{ marginBottom: '1rem' }}>
                    <Typography.Title level={5}>
                        Sobre qual <b>Defensor(a)</b>?
                    </Typography.Title>

                    <Select
                        style={{ width: '100%' }}
                        size="large"
                        onChange={setReportDefenderId}
                        placeholder="Selecione o Defensor"
                        disabled={!reportTemplateId}
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        value={reportDefenderId}
                        options={defenders.map(defender => ({
                            value: defender.id,
                            label: defender.user.full_name
                        }))}
                        onClear={() => setReportDefenderId(null)}
                    />
                </Row>

                <Row style={{ marginBottom: '1rem' }}>
                    <Typography.Title level={5}>
                        Sobre as atividades de qual <b>mês</b>?
                    </Typography.Title>

                    <DatePicker
                        style={{ width: '100%' }}
                        disabled={!reportDefenderId}
                        onChange={selectedMonth => {
                            if (!selectedMonth) {
                                setReportStartDate(null);
                                setReportEndDate(null);

                                return;
                            }

                            const [startDate, endDate] = [
                                selectedMonth.startOf('month').format('DD/MM/YYYY'),
                                selectedMonth.endOf('month').format('DD/MM/YYYY')
                            ];

                            setReportSelectedDate(selectedMonth);
                            setReportStartDate(startDate);
                            setReportEndDate(endDate);
                        }}
                        placeholder="Selecione o mês"
                        picker="month"
                        size="large"
                        format={{
                            format: 'MM/YYYY',
                            type: 'mask'
                        }}
                        disabledDate={current => {
                            const lastThreeMonths = dayjs().subtract(3, 'month').startOf('month');
                            const endOfLastMonth = dayjs().subtract(1, 'month').endOf('month');

                            return current.isBefore(lastThreeMonths, 'month') || current.isAfter(endOfLastMonth, 'month');
                        }}
                    />
                </Row>

                {(userCanAssignGroup && !reportIsTest) && (
                    <Row style={{ marginBottom: '1rem' }}>
                        <Space direction="vertical" size={0}>
                            <Typography.Title level={5} style={{ marginBottom: 0 }}>
                                Destinado a qual <b>gabinete</b>?
                            </Typography.Title>

                            <Typography.Text style={{ marginBottom: 8, display: 'block' }} type="secondary">
                                <small>O gabinete que será responsável pela análise deste RMA*</small>
                            </Typography.Text>
                        </Space>

                        <Select
                            style={{ width: '100%' }}
                            size="large"
                            onChange={setReportAssignedGroup}
                            placeholder="Selecione o gabinete"
                            disabled={!reportStartDate}
                            value={reportAssignedGroup}
                            options={groupOptions}
                        />
                    </Row>
                )}

                {userCanAssignGroup && (
                    <Space>
                        <Checkbox onChange={() => setReportIsTest(!reportIsTest)} />

                        <span>
                            🧪 Iniciar como um relatório de teste.
                            <a href="#" onClick={() => alert("Um relatório de teste somente poderá ser utilizado para conferência de dados.")}> Saiba mais</a>.
                        </span>
                    </Space>
                )}

                <FormModalAlert />
            </Modal>
        </>
    );
}
